import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useOrganization } from '@clerk/clerk-react'
import { AlertCircle, Clock, Rocket } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Spinner } from '@/components/ui/spinner'
import { useMountEffect } from '@/hooks/useMountEffect'
import {
  buildPeriodOptions,
  periodKey,
  periodOptionForKey,
  resolvePeriodParam,
  shouldStripMonthParam,
  utcMonthOf,
} from '../lib/periods'
import { useBillingPageData } from '../hooks/useBillingPageData'
import { useDutyCallCostsQuery } from '../hooks/useDutyCallCostsQuery'
import { buildDutyLines } from '../lib/dutyBreakdown'
import { useCurrentUsageQuery } from '../hooks/useCurrentUsageQuery'
import { BillingHeader } from './BillingHeader'
import { CreditsCard } from './CreditsCard'
import { RateCardCard } from './RateCardCard'
import { MinutesLedgerCard } from './MinutesLedgerCard'
import { MinutesRemainingCard } from './MinutesRemainingCard'
import { UtilizationTable } from './UtilizationTable'
import type { UtilizationGrouping } from './UtilizationTable'

const MONTH_PARAM = 'month'

/** Null-rendering helper mounted only while the URL's `?month=` should be
 *  removed (see shouldStripMonthParam): the deep link was invalid — garbage,
 *  a future month, or before the org's history — or redundantly names the
 *  current month, whose canonical URL has no param. Removes the param with a
 *  history replace so the URL matches the rendered view and Back doesn't
 *  return to the non-canonical link. Keyed by the raw value at the mount
 *  site so a newly-arriving strippable value re-runs the cleanup. */
function CleanMonthParam() {
  const [, setParams] = useSearchParams()
  useMountEffect(() => {
    setParams(
      (prev) => {
        const updated = new URLSearchParams(prev)
        updated.delete(MONTH_PARAM)
        return updated
      },
      { replace: true }
    )
  })
  return null
}

export function BillingPage() {
  const { organization } = useOrganization()
  // Always fetch current usage so the period selector knows how far back the
  // org's ledger goes. Until it loads, the dropdown shows only the current
  // month; once `earliest_period_start` arrives it expands to cover history.
  const currentUsageQuery = useCurrentUsageQuery(true)
  const earliestMonth = useMemo(() => {
    const raw = currentUsageQuery.data?.earliest_period_start
    return raw ? utcMonthOf(raw) : null
  }, [currentUsageQuery.data?.earliest_period_start])
  // The current month *key* (cheap per-render value) — not a Date — drives
  // the memo below and `isCurrent`, so a tab left open across a month
  // rollover rebuilds the options and stays pinned to the live calendar
  // month instead of looking the new month up in stale options (which would
  // flip `isCurrent` to false and query the month-end endpoint for the
  // in-flight month).
  const now = new Date()
  const currentPeriod = periodKey(now)
  const periodOptions = useMemo(
    () => buildPeriodOptions(utcMonthOf(`${currentPeriod}-01`), earliestMonth),
    [currentPeriod, earliestMonth]
  )
  // The selected period lives in the URL (`?month=YYYY-MM`) so a month view
  // is deep-linkable. The ledger range is only known once the usage query
  // succeeds; until then (loading OR errored — an error leaves the range
  // unknown, not empty) resolvePeriodParam trusts a well-formed past month
  // so a transient fetch failure doesn't strip a valid deep link.
  const [params, setParams] = useSearchParams()
  const rawMonth = params.get(MONTH_PARAM)
  const historyKnown = currentUsageQuery.isSuccess
  const period = resolvePeriodParam(rawMonth, periodOptions, historyKnown, now)
  const isCurrent = period === currentPeriod
  const data = useBillingPageData(period, isCurrent)
  // Whole workers, rounded as the home page's full time workers card rounds.
  const concurrentWorkers =
    data.concurrentFtes === null ? 0 : Math.round(data.concurrentFtes)
  const dutyCosts = useDutyCallCostsQuery(period, data.hasData)
  // The receipt opens in the shape it is invoiced in; the duty breakdown is
  // a drill-down from there. Its rows carry no duty split for that reason —
  // that is what the other view is for.
  const [grouping, setGrouping] = useState<UtilizationGrouping>('category')
  const utilizationLines = useMemo(
    () =>
      grouping === 'duty'
        ? buildDutyLines(data.utilizationLines, dutyCosts.data?.duties ?? [])
        : data.utilizationLines,
    [grouping, data.utilizationLines, dutyCosts.data]
  )
  // While the ledger range is still unknown, a trusted deep-linked past
  // month isn't in `periodOptions` yet, leaving the header Select with a
  // value that matches no item (it renders blank). Inject a placeholder
  // option for it until the real options arrive.
  const selectOptions = useMemo(
    () =>
      periodOptions.some((o) => o.value === period)
        ? periodOptions
        : [...periodOptions, periodOptionForKey(period)],
    [periodOptions, period]
  )

  const handlePeriodChange = useCallback(
    (next: string) => {
      setParams(
        (prev) => {
          const updated = new URLSearchParams(prev)
          if (next === periodKey(new Date())) updated.delete(MONTH_PARAM)
          else updated.set(MONTH_PARAM, next)
          return updated
        },
        { replace: true }
      )
    },
    [setParams]
  )

  const orgLabel = organization?.name ?? organization?.slug ?? '—'

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {shouldStripMonthParam(rawMonth, period, currentPeriod) && (
        <CleanMonthParam key={rawMonth} />
      )}
      <div className="flex-1 min-h-0 overflow-auto px-6 py-6">
        <div className="billing-print w-full max-w-5xl mx-auto">
          <BillingHeader
            orgLabel={orgLabel}
            period={period}
            periodOptions={selectOptions}
            onPeriodChange={handlePeriodChange}
            twilioPulledAt={data.twilioPulledAt}
            isCurrent={isCurrent}
          />

          {data.isLoading && !data.hasData ? (
            <div className="flex items-center justify-center py-24">
              <Spinner className="size-8 text-muted-foreground" />
            </div>
          ) : data.pendingFinalization ? (
            <Alert className="mb-6 border-amber-200 bg-amber-50 text-amber-900">
              <Clock className="text-amber-600" />
              <AlertDescription className="text-amber-900">
                <p>
                  This billing period is being reconciled. Final numbers will be
                  available within a few days of the month ending.
                </p>
              </AlertDescription>
            </Alert>
          ) : !data.hasData ? (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle />
              <AlertDescription>
                Unable to load billing data. Refresh the page or try again
                later.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <MinutesRemainingCard
                  ledger={data.ledger}
                  forecast={data.forecast}
                  isCurrent={isCurrent}
                />
                <MinutesLedgerCard
                  ledger={data.ledger}
                  forecast={data.forecast}
                  isCurrent={isCurrent}
                />
              </div>

              {concurrentWorkers > 0 && (
                <Alert className="mb-6 border-accent-border bg-accent-subtle text-accent-low-contrast">
                  <Rocket className="text-accent-solid" />
                  <AlertDescription className="text-accent-low-contrast">
                    {/* Wrap in <p> so AlertDescription's grid layout treats the text as a single item instead of stacking each child node on its own row. */}
                    <p>
                      You've been completing work at a rate of{' '}
                      <strong>
                        {concurrentWorkers} concurrent{' '}
                        {concurrentWorkers === 1 ? 'FTW' : 'FTWs'}
                      </strong>{' '}
                      {isCurrent ? 'this month' : 'in this period'}.
                    </p>
                  </AlertDescription>
                </Alert>
              )}

              <UtilizationTable
                lines={utilizationLines}
                grouping={grouping}
                onGroupingChange={setGrouping}
                dutyCostsFailed={dutyCosts.isError}
                totalMinutes={data.totalMinutes}
                totalUsd={data.totalUsd}
                asOfDate={data.asOfDate}
                isCurrent={isCurrent}
              />

              <CreditsCard
                period={period}
                isCurrent={isCurrent}
                asOfDate={data.asOfDate}
                earliestCreditDate={
                  currentUsageQuery.data?.earliest_credit_date
                }
              />

              {/* Keyed by period so the card starts collapsed on every month
                  instead of keeping the open state of the month viewed
                  before; switching to a cached month keeps hasData true, so
                  nothing else remounts it. */}
              <RateCardCard
                key={period}
                rateCard={data.rateCard}
                rateChanges={data.rateChanges}
                addons={data.addons}
                isCurrent={isCurrent}
              />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
